import { useMemo } from "react";
import {
  FlagCountResponse,
  GroupCount,
  ItemWithMetaData,
  TopicCount,
} from "../../api/Items";
import { useFamaFlags } from "../../api/FamaFlags";
import { classNames } from "../../utils";
import { SocialIcon } from "../../utils/social-icon";
import { DonutWithFlagsChart } from "./DonutWithFlags";
import { FlagsSummary } from "./ReportsSummaryFlagsSummary";
import { SummaryFlagText } from "./SummaryFlagText";
import {
  buildInsightChartData,
  insightLegendSegments,
} from "./insightChartData";
import { useLensTopicsEnabled } from "./useLensTopicsEnabled";

type Props = {
  flagsCount: FlagCountResponse | undefined;
  donutChartValues:
    | { color: string; label: string; value: number }[]
    | undefined;
  flagAmounts: { [key: string]: number } | undefined;
  profileList: ItemWithMetaData[] | undefined;
  keywordCount: GroupCount | undefined;
  topicCount?: TopicCount;
  companyUUID: string;
};

export const ReportSummaryHeaderNewFlags = ({
  flagsCount,
  donutChartValues,
  flagAmounts,
  profileList,
  keywordCount,
  topicCount,
}: Props) => {
  const { v3UiReportSummaryInsightRedesign } = useFamaFlags();
  const lensTopicsEnabled = useLensTopicsEnabled();
  const { chartData, segments, totalFlags } = useMemo(
    () =>
      buildInsightChartData(
        flagsCount,
        keywordCount,
        topicCount,
        lensTopicsEnabled
      ),
    [flagsCount, keywordCount, topicCount, lensTopicsEnabled]
  );
  const reportHasNoFlaggedContent =
    (flagsCount?.behaviors.total ?? 0) === 0 &&
    (keywordCount?.total ?? 0) === 0 &&
    (!lensTopicsEnabled || (topicCount?.total ?? 0) === 0);

  if (!donutChartValues) {
    return null;
  }

  const sortedBehaviors = donutChartValues
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="border rounded-md p-4 mb-4">
      <div>
        <div className="text-xl font-semibold text-gray-700 mb-2">
          How to Use This Report
        </div>
        <div className="text-gray-600 text-justify">
          This report provides an overview of flagged, online content from
          publicly available web presence. The inclusion of such content is
          based on the screening criteria established by the report requester in
          alignment with their internal policies, code of conduct, and
          applicable laws.
        </div>
      </div>
      <div className="mt-10">
        <div className="text-xl font-semibold text-gray-700 mb-5">
          Summary of Flagged Content
        </div>

        <FlagsSummary
          flagsCount={flagsCount}
          keywordCount={keywordCount}
          topicCount={topicCount}
        />
      </div>
      <div className="my-4 bg-gray-50 rounded-md p-5">
        {v3UiReportSummaryInsightRedesign ? (
          <>
            <div className="flex flex-col xl:flex-row min-w-0">
              <div className="flex items-center gap-4 flex-shrink-0 justify-center xl:justify-start">
                <div className="relative w-52 h-52">
                  <div className="absolute inset-0 flex flex-col justify-center items-center whitespace-nowrap">
                    <div
                      className="font-extrabold text-secondaryDarkBlue"
                      style={{ fontSize: "24px" }}
                    >
                      {totalFlags}
                    </div>
                    <div className="text-gray-500 text-sm">Total Items</div>
                  </div>
                  <DonutWithFlagsChart
                    data={chartData}
                    cutout="85%"
                    enableTooltip={true}
                    spacing={2}
                  />
                </div>

                <div
                  className="flex flex-col gap-2 flex-shrink-0"
                  style={{ fontFamily: "'Noto Sans', sans-serif" }}
                >
                  {insightLegendSegments(segments).map((s) => (
                      <div key={s.label} className="flex items-center">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: s.color }}
                        />
                        <span className="text-gray-600 text-sm mr-1 whitespace-nowrap">
                          {s.label}:
                        </span>
                        <span
                          className="text-sm font-semibold"
                          style={{ color: s.color }}
                        >
                          {s.value}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="flex-1 flex flex-col min-w-0 pt-6 pb-4 xl:pb-0 xl:pl-8 xl:ml-6 relative overflow-hidden">
                <div className="absolute left-0 top-8 bottom-8 w-0.5 bg-gray-200 hidden xl:block" />
                {(flagsCount?.behaviors.total ?? 0) > 0 && (
                  <>
                    <div className="font-bold text-gray-700 mb-6 text-center text-lg">
                      Behavior Flags
                    </div>
                    <div className="grid grid-cols-2 2xl:grid-cols-3 gap-x-4 2xl:gap-x-8 gap-y-4">
                      {sortedBehaviors.map((value, idx) => (
                        <div key={idx} className="flex items-center">
                          <span
                            className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: value.color }}
                          />
                          <span className="capitalize mr-1 text-gray-600">
                            {value.label.toLowerCase()}:
                          </span>
                          <span
                            className="font-semibold flex-shrink-0"
                            style={{ color: value.color }}
                          >
                            {flagAmounts?.[value.label] ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {reportHasNoFlaggedContent && (
                    <div className="text-lg flex flex-1 justify-center items-center text-gray-600">
                      Report contains no flag content.
                    </div>
                  )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-nowrap flex-shrink-0 min-w-0  ">
            <div
              className={classNames(
                "relative h-48 w-52  flex items-center  justify-center",
                "rounded-md p-2"
              )}
            >
              <div className="relative w-52 h-52">
                <div className="absolute top-0 left-0 whitespace-nowrap flex flex-col w-full h-full justify-center items-center">
                  <div
                    className={classNames(
                      "font-bold text-gray-800",
                      (flagsCount?.behaviors.total ?? 0) >= 100
                        ? "text-3xl"
                        : "text-5xl"
                    )}
                  >
                    {flagsCount?.behaviors.total ?? 0}
                  </div>
                  <div className="mr-1 text-gray-500 text-sm">Total Flags</div>
                </div>

                <div
                  className={classNames(
                    "relative w-52 h-52",
                    donutChartValues.length === 0 ? "pointer-events-none" : ""
                  )}
                >
                  {donutChartValues.length > 0 && (
                    <DonutWithFlagsChart
                      data={donutChartValues}
                      cutout={55}
                      enableTooltip={true}
                    />
                  )}
                  {donutChartValues.length === 0 && (
                    <DonutWithFlagsChart
                      data={[{ label: "No Data", value: 1, color: "#E5E7EB" }]}
                      cutout={55}
                      enableTooltip={true}
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-nowrap flex-col ml-6 overflow-clip flex-1 h-full items-center justify-center">
                {(flagsCount?.behaviors.total ?? 0) > 0 && (
                  <div className="flex flex-wrap mb-4 pt-3 gap-5 items-center">
                    {donutChartValues
                      .sort(function (a, b) {
                        if (a.label < b.label) {
                          return -1;
                        }
                        if (a.label > b.label) {
                          return 1;
                        }
                        return 0;
                      })
                      .map((value: any, idx) => {
                        return (
                          <div key={idx} className="flex items-center">
                            <span
                              className={classNames(
                                "inline-block w-2 h-2 rounded-full mr-2",
                                value.color
                              )}
                              style={{
                                backgroundColor: value.color,
                              }}
                            ></span>
                            <span className="capitalize mr-2">
                              {value.label.toLowerCase()}
                            </span>
                            <span
                              style={{
                                color: value.color,
                              }}
                            >
                              {flagAmounts?.[value.label] ?? 0}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
                {reportHasNoFlaggedContent && (
                    <div className="text-lg flex flex-1 justify-center items-center text-gray-600">
                      Report contains no flag content.
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
        <div className="">
          <div>
            <SummaryFlagText
              profileList={profileList ?? []}
              donutChartValues={donutChartValues ?? []}
              keywordCount={keywordCount}
              topicCount={topicCount}
              flagsCount={flagsCount}
            />
          </div>
        </div>
      </div>
      <div>
        <div className="text-xl font-semibold text-gray-700 my-5">
          Confirmed Profiles: {profileList?.length ?? 0}
        </div>
        <div className="list-none">
          {profileList
            ?.sort((a, b) => {
              const aIsClient = a.item.content.added_by === "CLIENT";
              const bIsClient = b.item.content.added_by === "CLIENT";

              if (aIsClient && !bIsClient) {
                return -1;
              }
              if (!aIsClient && bIsClient) {
                return 1;
              }

              return a.item.content.url.localeCompare(b.item.content.url);
            })
            .map((item: ItemWithMetaData, idx) => {
              return (
                <div className="my-4 flex items-center gap-3" key={idx}>
                  <SocialIcon
                    style={{ width: "20px", height: "20px" }}
                    url={item.item.content.url}
                  />

                  <a
                    href={item.item.content.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-gray-600"
                  >
                    {item.item.content.url}
                  </a>
                  <span className="text-gray-500 text-xs">
                    {item.item.content.added_by === "CLIENT" ? (
                      <span className="bg-blue-100 text-gray-800 px-2 py-1 rounded-md uppercase">
                        Provided
                      </span>
                    ) : (
                      <span className="bg-green-100 text-gray-800 px-2 py-1 rounded-md uppercase">
                        Discovered
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          {(profileList || []).length === 0 && (
            <li className="my-2" key="1">
              No Confirmed Profiles found.
            </li>
          )}
        </div>
      </div>
    </div>
  );
};
